import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  DollarSign, 
  Mic2,
  Radio,
  Globe,
  Headphones,
  Rocket
} from 'lucide-react';

const API_URL = '';  // Use relative URLs with proxy

const BecomeReviewer = () => {
  const navigate = useNavigate();
  const [twitchData, setTwitchData] = useState(null);
  const [showTwitchEmbed, setShowTwitchEmbed] = useState(false);
  
  useEffect(() => {
    // Fetch Twitch status from our API
    fetch(`${API_URL}/api/twitch/status`)
      .then(res => res.json())
      .then(data => {
        console.log('Twitch status:', data);
        setTwitchData(data);
      })
      .catch(err => {
        console.error('Failed to fetch Twitch status:', err);
        // Fallback to channel embed
        setTwitchData({
          type: 'channel',
          channel: 'jxhnjacxb',
          isLive: false
        });
      });
  }, []);

  const handleStartReviewing = () => {
    navigate('/signup?type=reviewer');
  };

  const getTwitchEmbedUrl = (data) => {
    if (!data) return '';
    
    const parents = [
      window.location.hostname,
      'miccheckmillionaire.com',
      'www.miccheckmillionaire.com'
    ].filter(p => p); // Remove empty strings
    
    const parentParams = parents.map(p => `parent=${p}`).join('&');
    
    if (data.type === 'vod' && data.videoId) {
      return `https://player.twitch.tv/?video=${data.videoId}&${parentParams}&muted=false`;
    } else if (data.type === 'live' && data.channel) {
      return `https://player.twitch.tv/?channel=${data.channel}&${parentParams}&muted=false`;
    } else if (data.type === 'channel' && data.channel) {
      return `https://player.twitch.tv/?channel=${data.channel}&${parentParams}&muted=false`;
    }
    
    return '';
  };

  const features = [
    {
      icon: <DollarSign className="h-8 w-8 text-[#F2D857]" />,
      title: 'Get Paid to Give Feedback',
      description: 'Earn money sharing your honest opinions on music submissions. Every review helps artists improve — and builds your income stream.'
    },
    {
      icon: <Globe className="h-8 w-8 text-[#F2D857]" />,
      title: 'Grow Your Network',
      description: 'Connect with artists, producers, and other reviewers from around the world. Collaborate, get discovered, and grow your influence.'
    },
    {
      icon: <Headphones className="h-8 w-8 text-[#F2D857]" />,
      title: 'Monetize Your Ears',
      description: 'Turn your ear for talent into a business. Review songs, offer critiques, and earn from every session — live or on-demand.'
    },
    {
      icon: <Rocket className="h-8 w-8 text-[#F2D857]" />,
      title: 'Shape the Future of Music Discovery',
      description: 'Be part of a movement redefining how independent artists get heard and how reviewers get paid.'
    }
  ];

  const steps = [
    {
      number: '1',
      title: 'Sign Up',
      description: 'Create your reviewer profile and set your schedule for live sessions.'
    },
    {
      number: '2',
      title: 'Start Reviewing',
      description: 'Accept submissions, stream your reviews, and offer real-time feedback.'
    },
    {
      number: '3',
      title: 'Get Paid',
      description: 'Earn through tips, subscriptions, and paid review slots — directly and securely.'
    }
  ];

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Become a Paid Music Reviewer | Mic Check Millionaire</title>
        <meta 
          name="description" 
          content="Turn your musical expertise into income. Join Mic Check Millionaire to become a paid music reviewer: live sessions, artist feedback & revenue opportunities."
        />
        <meta name="keywords" content="paid music reviewer, get paid for music feedback, music reviewer jobs, live music review sessions, music feedback platform" />
        <meta property="og:title" content="Become a Paid Music Reviewer | Mic Check Millionaire" />
        <meta property="og:description" content="Turn your musical expertise into income. Join Mic Check Millionaire to become a paid music reviewer." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://mic-check-millionaire.com/become-reviewer" />
        
        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CreativeWork",
            "name": "Mic Check Millionaire - Paid Music Reviewer Platform",
            "description": "Platform connecting music reviewers with artists for paid feedback sessions",
            "provider": {
              "@type": "Organization",
              "name": "Mic Check Millionaire"
            },
            "offers": {
              "@type": "Offer",
              "description": "Become a paid music reviewer and earn income providing expert feedback"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <div className="bg-black text-white py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-6xl font-bold font-montserrat mb-6">
                Why Become a Music Reviewer?
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 leading-relaxed mb-8">
                Join a thriving community where your passion for music turns into consistent income. Share your expertise, connect with artists, and get paid to help shape the sound of tomorrow.
              </p>
              <Button
                size="lg"
                onClick={handleStartReviewing}
                className="bg-[#F2D857] hover:bg-[#F2D857]/90 text-black text-lg px-10 py-6 rounded-xl font-bold shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
                data-testid="hero-start-reviewing-btn"
              >
                <Mic2 className="mr-2 h-5 w-5" />
                Start Reviewing Now
              </Button>
            </div>
          </div>
        </div>

        {/* Twitch Live Preview */}
        <div className="bg-gray-50 py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <Card className="overflow-hidden border-2 border-gray-200 shadow-xl">
                <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="bg-white/20 p-2 rounded-lg backdrop-blur-sm">
                        <Radio className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-white font-bold text-lg">
                          {twitchData?.isLive ? 'Live Review Session' : 'Recent Review Session'}
                        </h3>
                        <p className="text-purple-100 text-sm">
                          {twitchData?.isLive ? 'Watch JxhnJacxb review submissions live' : 'Replay the latest session'}
                        </p>
                      </div>
                    </div>
                    {twitchData?.isLive && (
                      <Badge className="bg-red-500 text-white hover:bg-red-500 animate-pulse">
                        <Radio className="h-3 w-3 mr-1" />
                        LIVE
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="relative bg-black" style={{ paddingTop: '56.25%' }}>
                  {twitchData && !showTwitchEmbed ? (
                    <div 
                      className="absolute top-0 left-0 w-full h-full flex items-center justify-center cursor-pointer bg-gradient-to-br from-purple-900 to-black"
                      onClick={() => setShowTwitchEmbed(true)}
                    >
                      <div className="text-center">
                        <div className="w-20 h-20 bg-[#F2D857] rounded-full flex items-center justify-center mx-auto mb-4 hover:scale-110 transition-transform">
                          <svg className="w-10 h-10 text-black ml-1" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z"/>
                          </svg>
                        </div>
                        <p className="text-white font-medium">Click to load stream</p>
                        <p className="text-gray-400 text-sm mt-1">Watch live sessions & highlights</p>
                      </div>
                    </div>
                  ) : twitchData && showTwitchEmbed ? (
                    <iframe
                      src={getTwitchEmbedUrl(twitchData)}
                      className="absolute top-0 left-0 w-full h-full"
                      allow="autoplay; fullscreen"
                      allowFullScreen
                      frameBorder="0"
                      title="Twitch player"
                      loading="lazy"
                    />
                  ) : null}
                </div>
              </Card>
            </div>
          </div>
        </div>

        {/* Feature Boxes */}
        <div className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8">
                {features.map((feature, index) => (
                  <Card key={index} className="p-8 border-2 border-gray-200 hover:border-[#F2D857] transition-all duration-300 hover:shadow-xl">
                    <div className="flex items-start gap-4">
                      <div className="bg-black p-4 rounded-xl">
                        {feature.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-gray-50 py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-5xl font-bold font-montserrat mb-4">
                  How It Works
                </h2>
                <p className="text-xl text-gray-600">
                  How to Start Reviewing on Mic Check Millionaire
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {steps.map((step, index) => (
                  <div key={index} className="text-center">
                    <div className="relative mb-6">
                      <div className="w-20 h-20 bg-[#F2D857] rounded-full flex items-center justify-center mx-auto shadow-lg">
                        <span className="text-4xl font-bold text-black">{step.number}</span>
                      </div>
                      {index < steps.length - 1 && (
                        <div className="hidden md:block absolute top-10 left-1/2 w-full h-0.5 bg-gray-300" style={{ transform: 'translateX(50%)' }}></div>
                      )}
                    </div>
                    <h3 className="text-2xl font-bold mb-3">{step.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{step.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-16 md:py-24 bg-black text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-5xl font-bold font-montserrat mb-6">
                Ready to Turn Your Passion Into Profit?
              </h2>
              <p className="text-xl text-gray-300 mb-10 leading-relaxed">
                Join the fastest-growing network of paid music reviewers. Build your reputation, grow your audience, and earn income doing what you love — listening to great music.
              </p>
              <Button 
                size="lg"
                onClick={handleStartReviewing}
                className="bg-[#F2D857] hover:bg-[#F2D857]/90 text-black text-xl px-12 py-8 rounded-xl font-bold shadow-2xl hover:scale-105 transition-all duration-300 w-full md:w-auto"
                aria-label="Sign up to become a paid music reviewer on Mic Check Millionaire"
              >
                <Mic2 className="mr-3 h-6 w-6" />
                🎧 Start Reviewing Now
              </Button>
            </div>
          </div>
        </div>

        {/* Additional SEO Content */}
        <div className="bg-gray-50 py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center text-sm text-gray-600">
              <p className="mb-2">
                Mic Check Millionaire connects <strong>paid music reviewers</strong> with independent artists seeking expert <strong>music feedback</strong>. Our platform offers <strong>music reviewer jobs</strong> and <strong>live music review sessions</strong> that help you <strong>get paid for music feedback</strong> while building your reputation in the industry.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BecomeReviewer;
